<template>
    <div class="print-page" :class="{ 'print-page--ready': ready }">
        <div v-if="loading" class="print-status">
            <ChatPreloader />
            <p>Загружаем сообщения…</p>
        </div>

        <div v-else-if="error" class="print-status print-status--error">
            <p>Не удалось получить историю треда.</p>
        </div>

        <template v-else>
            <!--
                Блок для html2pdf: контейнер `.print-doc` и есть корень
                снимка. Все стили должны быть глобальными (см. <style> без scoped),
                иначе html2canvas не увидит их в клонированной ноде.
            -->
            <div ref="docRef" class="print-doc">
                <header class="print-header">
                    <div class="print-header__left">
                        <!--
                            Раньше здесь дублировался текстовый бейдж «AM · AI-аналитик»
                            — визуально повторяет `.print-header__tag` справа под лого.
                            Убираем текстовый вариант, оставляем сам логотип +
                            подпись «AI-АНАЛИТИК» в правой колонке.
                        -->
                        <h1 class="print-header__title">{{ documentTitle }}</h1>
                        <div v-if="documentSubtitle" class="print-header__subtitle">
                            {{ documentSubtitle }}
                        </div>
                        <div class="print-header__meta">
                            Сгенерировано {{ generatedAt }} · {{ messagesToRender.length }} {{ messageWord }}
                        </div>
                    </div>
                    <!-- Логотип бренда — тот же файл, что в сайдбаре чата / шапке продукта. -->
                    <div class="print-header__right">
                        <img :src="logoSrc" alt="Автомаркетолог" class="print-header__logo" />
                        <span class="print-header__tag">AI-АНАЛИТИК</span>
                    </div>
                </header>

                <main class="print-body">
                    <ChatMessage
                        v-for="msg in messagesToRender"
                        :key="msg.id"
                        :message="msg"
                        :user-initials="userInitials"
                        :user-name="userName"
                        report-view
                    />
                </main>

                <footer class="print-footer">
                    <span>AM · AI-аналитик</span>
                    <span>{{ generatedAt }}</span>
                </footer>
            </div>

            <!--
                Плавающая шапка над печатью — не попадает в PDF (спрятана @media print
                и хранится вне docRef).
            -->
            <div class="print-toolbar no-print">
                <div v-if="uploadStatus" class="upload-status" :class="uploadStatusClass">
                    {{ uploadStatus }}
                </div>
                <button
                    v-if="!uploadMode"
                    type="button"
                    class="btn primary"
                    @click="triggerPrint"
                >
                    <ChatIcon name="download" :size="14" />
                    Печать / Сохранить PDF
                </button>
                <button
                    v-else
                    type="button"
                    class="btn primary"
                    :disabled="uploading || uploadDone"
                    @click="triggerUpload"
                >
                    <ChatIcon name="download" :size="14" />
                    {{ uploading ? 'Формируем PDF…' : uploadDone ? 'Сохранено' : 'Скачать' }}
                </button>
                <button type="button" class="btn ghost" @click="closeWindow">Закрыть</button>
            </div>
        </template>
    </div>
</template>

<script setup>
/*
 * Печать / экспорт треда в PDF.
 *
 * Работает в двух режимах:
 *  1) `?upload=1` (используется из ChatExportModal): после рендера мы сами
 *     генерируем PDF через html2pdf.js и заливаем его на /api/chat/exports,
 *     чтобы файл появился в разделе «Мои файлы». Пользователь видит статус
 *     операции и не пользуется системным диалогом печати.
 *  2) Без `upload=1` — старое поведение: открываем `window.print()`, юзер
 *     сохраняет как PDF сам. Оставлено на случай отладки.
 *
 * URL: /chat/print?thread=<uuid>&ids=<csv>&title=<encoded>&subtitle=<encoded>&upload=1&filename=<name>
 */
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore as useVuexStore } from 'vuex'
import ChatMessage from '@/components/chat/conversation/ChatMessage.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatPreloader from '@/components/chat/ChatPreloader.vue'
import { getChatHistory, createExport } from '@/utils/chatApi'
import { rebuildMessageFromServer } from '@/components/chat/state/useChatStore'
import LogoImage from '@/assets/images/am_logo 1.svg'

// Тот же логотип, что и на главной странице / в сайдбаре чата
// (см. ChatSidebarBrand.vue, MainHeader.vue).
const logoSrc = LogoImage

// Лимит на размер PDF: держим синхронно с бэком (ChatExportController::store,
// max:25600 → 25 МБ). Если сгенерённый blob больше — не пытаемся отправлять
// на /api/chat/exports, сразу говорим пользователю понятной формулировкой.
const MAX_EXPORT_BYTES = 25 * 1024 * 1024

const route = useRoute()
const vuexStore = useVuexStore()

const loading = ref(true)
const error = ref(false)
const messages = ref([])
const ready = ref(false)
const docRef = ref(null)

// Upload-режим (запуск из «Мои файлы»).
const uploadMode = computed(() => (route.query.upload || '').toString() === '1')
const uploading = ref(false)
const uploadDone = ref(false)
const uploadStatus = ref('')
const uploadStatusClass = ref('')

const documentTitle = computed(() => {
    const t = (route.query.title || '').toString().trim()
    return t || 'Экспорт диалога'
})

const documentSubtitle = computed(() => (route.query.subtitle || '').toString().trim())

const generatedAt = computed(() => new Date().toLocaleString('ru-RU'))

const userInfo = computed(() => vuexStore.getters['main/userInfo'] || {})
const userName = computed(() => userInfo.value?.name || 'Аналитик')
const userInitials = computed(() => {
    const source = userName.value || ''
    const parts = source.split(' ').filter(Boolean)
    if (parts.length === 0) return 'AM'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[1][0]).toUpperCase()
})

const requestedIds = computed(() => {
    const raw = (route.query.ids || '').toString()
    if (!raw.trim()) return null
    const ids = raw.split(',')
        .map(s => Number(s.trim()))
        .filter(n => Number.isFinite(n))
    return ids.length ? new Set(ids) : null
})

const messagesToRender = computed(() => {
    const list = messages.value
    if (!requestedIds.value) return list
    // Селекшн в чате разрешён только на AI-сообщениях (см. ChatMessage.vue,
    // canSelect=false для role='user'), поэтому в query.ids приходят только
    // serverId ассистентских ответов. Но экспортировать «голый» AI без
    // исходного запроса бессмысленно — читатель PDF потеряет контекст. Здесь
    // расширяем keep-set так, чтобы вместе с каждым выбранным AI-ответом в
    // документ попадал и предшествующий ему user-запрос.
    const keep = new Set(requestedIds.value)
    let lastUserId = null
    for (const m of list) {
        if (m.role === 'user') {
            lastUserId = m.serverId
            continue
        }
        if (m.role === 'ai' && keep.has(m.serverId) && lastUserId) {
            keep.add(lastUserId)
        }
    }
    return list.filter(m => keep.has(m.serverId))
})

/**
 * Итоговое имя файла для скачивания и записи в «Мои файлы». Приходит из
 * ChatExportModal (единый источник истины) — на бэк уйдёт ровно то же.
 */
const finalFilename = computed(() => {
    const raw = (route.query.filename || '').toString().trim()
    if (raw) return raw
    // Fallback — если страницу открыли вручную без ?filename= (например, для отладки).
    const now = new Date()
    const pad = n => String(n).padStart(2, '0')
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
    const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
    return `chat_thread_${date}_${time}.pdf`
})

function plural(n, forms) {
    const abs = Math.abs(n) % 100
    if (abs >= 11 && abs <= 14) return forms[2]
    const last = abs % 10
    if (last === 1) return forms[0]
    if (last >= 2 && last <= 4) return forms[1]
    return forms[2]
}

const messageWord = computed(() => plural(messagesToRender.value.length, ['сообщение', 'сообщения', 'сообщений']))

async function loadHistory() {
    const threadId = (route.query.thread || '').toString().trim()
    if (!threadId) {
        error.value = true
        loading.value = false
        return
    }
    loading.value = true
    error.value = false
    try {
        const data = await getChatHistory(threadId)
        messages.value = (data.messages || []).map(rebuildMessageFromServer)
        if (messagesToRender.value.length === 0) {
            error.value = true
        }
    } catch (e) {
        error.value = true
        messages.value = []
    } finally {
        loading.value = false
    }
}

/**
 * Ждём, пока echarts дорисует все графики. Простое requestAnimationFrame ×2
 * не всегда достаточно (canvas-рендер echarts асинхронный, особенно на тяжёлых
 * дашбордах), поэтому дублируем задержкой в 700 мс — эмпирически стабильно.
 */
async function waitForRender() {
    await nextTick()
    await new Promise(resolve => requestAnimationFrame(resolve))
    await new Promise(resolve => requestAnimationFrame(resolve))
    await new Promise(resolve => setTimeout(resolve, 700))
}

async function triggerPrint() {
    await waitForRender()
    window.print()
}

/**
 * Генерирует PDF из docRef через html2pdf.js и заливает на бэк.
 *
 * html2pdf.js подгружаем динамически: библиотека тяжёлая (jspdf + html2canvas),
 * и её не нужно тащить в основной бандл (ChatPage не использует).
 */
/**
 * Инициирует немедленное скачивание готового Blob'а браузером.
 * Используем «скрытая <a> + click()» вместо window.open(URL.createObjectURL(...)):
 * так браузер понимает атрибут `download`, ставит наш `finalFilename` и не
 * пытается отрендерить PDF во встроенном viewer'е.
 */
function downloadBlobLocally(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    // rel=noopener на всякий случай — вдруг download проигнорируется и браузер
    // откроет ссылку в новом контексте.
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    // revoke чуть позже — некоторые браузеры отменяют скачивание, если revoke
    // выполнить прямо в микротаске после click().
    setTimeout(() => URL.revokeObjectURL(url), 4000)
}

async function triggerUpload() {
    if (uploading.value || uploadDone.value) return
    if (!docRef.value) return

    // Guard: без треда или без сообщений upload точно не сделаем, а вкладка
    // может открыться с битой ссылкой (например, /chat/print?thread=&ids=).
    // Не хотим ловить невнятный «field is required» с бэка — сразу говорим
    // пользователю, что не так.
    const threadId = (route.query.thread || '').toString().trim()
    const ids = Array.from(requestedIds.value || [])
    if (!threadId) {
        uploadStatus.value = 'Не хватает данных: не выбран диалог для экспорта.'
        uploadStatusClass.value = 'status-error'
        return
    }

    uploading.value = true
    uploadStatus.value = 'Формируем PDF…'
    uploadStatusClass.value = 'status-info'

    try {
        await waitForRender()
        const { default: html2pdf } = await import('html2pdf.js')

        const blob = await html2pdf()
            .set({
                margin: [10, 10, 12, 10], // mm; чуть больше снизу под footer
                filename: finalFilename.value,
                // PNG вместо JPEG: jpeg-компрессия «съедает» тонкий тёмный
                // текст на светлом фоне (особенно с scale: 2). PNG весит
                // немного больше, но текст в PDF гарантированно читается.
                image: { type: 'png', quality: 1 },
                html2canvas: {
                    // dpi 2× — векторно echarts не отдаст (canvas), но при
                    // печати на A4 текст выглядит нормально.
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    // letterRendering: браузер рендерит каждую букву отдельно,
                    // не растеризуя строку целиком — так надёжнее для узких
                    // блоков (.user-bubble) и переводов строк.
                    letterRendering: true,
                    // logging=false, чтобы html2canvas не шумел в console.
                    logging: false,
                },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['css', 'legacy'] },
            })
            .from(docRef.value)
            .outputPdf('blob')

        // Сначала — локальное скачивание. Пользователь должен получить файл,
        // даже если S3 сейчас недоступен (см. историю с отсутствующим
        // aws-sdk-php в vendor'e). Это делает UX «понятным по умолчанию»:
        // «нажал экспорт → файл в загрузках», а «Мои файлы» — приятный бонус.
        downloadBlobLocally(blob, finalFilename.value)

        // Файл больше 25 МБ — на бэк не отправляем (валидация в
        // ChatExportController::store `max:25600` отклонит запрос, но лучше
        // не тратить upload и не пугать пользователя невнятным 422). Формулируем
        // ровно так, как просил шеф: «файл больше 25 МБ, не получилось сохранить».
        if (blob.size > MAX_EXPORT_BYTES) {
            uploadDone.value = true
            uploadStatus.value = 'Файл скачан. Файл больше 25 МБ, не получилось сохранить в «Мои файлы».'
            uploadStatusClass.value = 'status-warn'
            return
        }

        uploadStatus.value = 'Файл скачан. Дублируем в «Мои файлы»…'

        try {
            await createExport({
                threadExternalId: threadId,
                blob,
                filename: finalFilename.value,
                title: documentTitle.value,
                messageIds: ids,
            })
            uploadDone.value = true
            uploadStatus.value = 'Готово! Файл скачан и сохранён в «Мои файлы».'
            uploadStatusClass.value = 'status-ok'
        } catch (uploadErr) {
            // Даже если upload на бэк не удался — юзер уже получил файл. Не
            // теряем это позитивное состояние, просто сообщаем про частичный
            // сбой (полезно для дебага и чтобы юзер понимал, почему в «Мои
            // файлы» файл не появился). Отдельно ловим 413/422 «file too large»
            // — бэковый лимит совпадает с MAX_EXPORT_BYTES, но перестраховываемся.
            uploadDone.value = true
            const httpStatus = uploadErr?.response?.status
            const tooLarge = httpStatus === 413
                || (httpStatus === 422 && /file|размер|large|too big/i.test(uploadErr?.message || ''))
            uploadStatus.value = tooLarge
                ? 'Файл скачан. Файл больше 25 МБ, не получилось сохранить в «Мои файлы».'
                : 'Файл скачан. В «Мои файлы» сохранить не удалось: '
                    + (uploadErr?.message || 'ошибка сети')
            uploadStatusClass.value = 'status-warn'
            // eslint-disable-next-line no-console
            console.error('chat export upload failed', uploadErr)
        }
    } catch (e) {
        uploadStatus.value = (e && e.message)
            ? `Ошибка: ${e.message}`
            : 'Не удалось сформировать PDF. Попробуйте ещё раз.'
        uploadStatusClass.value = 'status-error'
        // eslint-disable-next-line no-console
        console.error('chat export pdf failed', e)
    } finally {
        uploading.value = false
    }
}

function closeWindow() {
    try { window.close() } catch (e) { /* ignore */ }
}

onMounted(async () => {
    document.title = documentTitle.value

    await loadHistory()
    if (error.value) return

    ready.value = true

    // Автозапуск сознательно убран (фидбэк шефа): пользователь всегда нажимает
    // кнопку «Скачать / Сохранить» сам — иначе браузер молча инициирует
    // тяжёлую операцию (несколько сотен мс html2canvas + upload), а пользователь
    // теряет ощущение контроля.
})
</script>

<style>
/*
    Печатные стили сознательно НЕ scoped: `window.print()` и html2canvas
    хеши scoped-стилей теряют, глобальный <style> гарантированно применится.
*/
html, body {
    background: #ffffff;
    margin: 0;
    padding: 0;
    color: #23282C;
    font-family: 'Inter', 'Segoe UI', 'Roboto', -apple-system, sans-serif;
}

.print-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px 96px;
}

.print-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 12px;
    color: #5f5f5f;
}

.print-status--error {
    color: #C0050F;
}

.print-doc {
    width: 100%;
    max-width: 794px; /* ширина A4 при 96dpi */
    background: white;
    padding: 24px 28px;
    box-sizing: border-box;
}

.print-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    border-bottom: 2px solid #23282C;
    padding-bottom: 14px;
    margin-bottom: 22px;
}

.print-header__left {
    flex: 1;
    min-width: 0;
}

.print-header__right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
    padding-top: 2px;
}

.print-header__logo {
    height: 40px;
    width: auto;
    max-width: 160px;
    display: block;
}

.print-header__tag {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.18em;
    color: #E30613;
    text-transform: uppercase;
}

.print-header__brand {
    font-size: 11px;
    letter-spacing: 0.18em;
    font-weight: 700;
    text-transform: uppercase;
    color: #E30613;
    margin-bottom: 10px;
}

.print-header__title {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.25;
    color: #23282C;
    margin: 0;
}

.print-header__subtitle {
    font-size: 12px;
    color: #5f5f5f;
    margin-top: 6px;
    line-height: 1.4;
}

.print-header__meta {
    font-size: 11px;
    color: #888;
    margin-top: 10px;
    letter-spacing: 0.02em;
}

.print-body {
    display: block;
}

/*
 * html2canvas при клонировании DOM теряет scoped-хеши vue-стилей, поэтому цвет
 * текста и фон .user-bubble/.user-text подстраховываем глобально. Без этого в
 * рендере получалась «пустая пилюля» (сам блок есть, но текст либо белый на
 * белом, либо просто не отрисован из-за отсутствия computed color).
 */
.print-body .user-bubble {
    display: block !important;
    background: #ffffff !important;
    color: #23282C !important;
    border: 1px solid #e0e0e0 !important;
    box-shadow: none !important;
    padding: 10px 16px !important;
    border-radius: 12px !important;
    max-width: 100% !important;
    width: fit-content;
}

.print-body .user-text {
    color: #23282C !important;
    background: transparent !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    white-space: pre-wrap !important;
    word-break: break-word !important;
    min-height: 1.6em;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
}

.print-body .message {
    padding: 14px 0 20px;
    border-bottom: 1px dashed #e0e0e0;
    page-break-inside: auto;
    break-inside: auto;
}

.print-body .message:last-child {
    border-bottom: 0;
}

.print-body .card,
.print-body .callout,
.print-body .chart-container,
.print-body .followups {
    page-break-inside: avoid;
    break-inside: avoid;
}

.print-footer {
    display: flex;
    justify-content: space-between;
    color: #888;
    font-size: 10px;
    margin-top: 24px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
}

.print-toolbar {
    position: fixed;
    right: 24px;
    top: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 100;
}

.upload-status {
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 12.5px;
    color: #23282C;
    max-width: 320px;
}
.upload-status.status-ok { border-color: #0f9d58; color: #0f7a45; background: #f2fbf5; }
.upload-status.status-error { border-color: #C0050F; color: #C0050F; background: #fff5f6; }
.upload-status.status-info { border-color: #23282C; }
/* Локально скачали, но не смогли залить на бэк — жёлтый «частичный» статус. */
.upload-status.status-warn { border-color: #b7791f; color: #7a4a00; background: #fff8e6; }

.btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
    font-size: 13px;
    padding: 8px 14px;
    border-radius: 6px;
    border: 0;
    cursor: pointer;
}

.btn.primary {
    background: #E30613;
    color: #fff;
    font-weight: 600;
}
.btn.primary:hover:not(:disabled) { background: #C0050F; }
.btn.primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn.ghost {
    background: white;
    border: 1px solid #e0e0e0;
    color: #23282C;
}
.btn.ghost:hover { background: #f0f0f0; }

@media print {
    .no-print { display: none !important; }

    html, body { background: white !important; }
    .print-page { padding: 0; }
    .print-doc { max-width: 100%; width: 100%; padding: 0 8mm; margin: 0; }
    @page { size: A4; margin: 12mm; }
}
</style>
